import React, { useState } from 'react';
import { Box, Typography, Stack, ButtonBase, MenuItem, Icon } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import MenuPopover from '../../../components/extras/MenuPopover';
import { reduxHelper } from '../../../config/reduxHelper';

/**
 * FlexibleDropdownBlock - A flexible replacement for CustomPopover.
 * Supports a main label/icon and a dropdown menu with children links.
 */
const FlexibleDropdownBlock = ({ block, isSiteBuilder, onUpdate }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const { push } = useRouter();
    const dispatch = useDispatch();
    const { siteBuilderSelectedChild, pageJson } = useSelector((state) => state.reducer);

    const isSelected = isSiteBuilder && siteBuilderSelectedChild?.uid === block.uid;
    const fontFamily = pageJson?.pageStyle?.fontFamily || 'Roboto';

    const handleOpen = (event) => {
        if (isSiteBuilder) {
            event.stopPropagation();
            if (siteBuilderSelectedChild?.uid !== block.uid) {
                dispatch({
                    type: reduxHelper.UPDATE_SITE_BUILDER_SELECTED_CHILD,
                    payload: { ...block },
                });
            }
        }
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleItemClick = (item) => {
        handleClose();
        if (isSiteBuilder) return;

        if (item.url) {
            if (typeof item.url === 'object') {
                if (item.url.target) {
                    window.open(item.url.path, "_blank", "noopener,noreferrer");
                } else {
                    push(item.url.path);
                }
            } else {
                push(item.url);
            }
        }
    };

    const variant = block.props?.variant; // 'icon' or 'text'
    const children = block.children || [];

    return (
        <Box
            sx={{
                display: 'inline-flex',
                outline: isSelected ? '1px solid #1890ff' : 'none',
                outlineOffset: '2px',
                borderRadius: '4px',
                transition: 'all 0.2s',
                '&:hover': {
                    bgcolor: isSiteBuilder ? 'rgba(24, 144, 255, 0.05)' : 'transparent',
                },
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                component={ButtonBase}
                onClick={handleOpen}
                sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    gap: 0.5,
                    ...block.props?.sx
                }}
            >
                {variant === 'icon' ? (
                    <Icon
                        className={`mdi mdi-${anchorEl ? (block.props?.focusedIcon || 'close') : (block.props?.unFocusedIcon || 'menu')}`}
                        sx={{
                            fontSize: block.props?.fontSize || '24px',
                            color: block.props?.style?.color || block.props?.color || 'inherit',
                        }}
                    />
                ) : (
                    <>
                        <Typography
                            contentEditable={isSiteBuilder}
                            suppressContentEditableWarning={true}
                            onClick={(e) => {
                                if (isSiteBuilder) {
                                    e.stopPropagation(); // Prevent ButtonBase from opening the menu
                                }
                            }}
                            onBlur={(e) => {
                                if (isSiteBuilder) {
                                    onUpdate(block.uid, { label: e.target.innerText });
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.target.blur();
                                }
                            }}
                            sx={{
                                fontFamily: `${fontFamily} !important`,
                                fontSize: block.props?.fontSize || 'inherit',
                                fontWeight: block.props?.fontWeight || 'inherit',
                                color: block.props?.style?.color || block.props?.color || 'inherit',
                                minWidth: '20px',
                                outline: 'none',
                            }}
                        >
                            {block.label || 'Dropdown'}
                        </Typography>
                        <ArrowDropDown sx={{ color: block.props?.style?.color || block.props?.color || 'inherit' }} />
                    </>
                )}
            </Stack>

            <MenuPopover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                sx={{
                    p: 1,
                    mt: 1.5,
                    ml: 0.75,
                    '& .MuiMenuItem-root': {
                        typography: 'body2',
                        borderRadius: 0.75,
                        fontFamily: `${fontFamily} !important`,
                    },
                }}
            >
                {children.length > 0 ? (
                    children.map((item, index) => (
                        <MenuItem key={index} onClick={() => handleItemClick(item)}>
                            {item.label}
                        </MenuItem>
                    ))
                ) : (
                    <MenuItem disabled>No items</MenuItem>
                )}
            </MenuPopover>
        </Box>
    );
};

export default FlexibleDropdownBlock;
